import { useCallback, useEffect, useRef, useState } from 'react';
import { audioDebug } from '../voice/debug-overlay';

interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionInstance;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

export function useSpeechToText(
  onTranscript: (text: string) => void,
  opts?: { finalOnly?: boolean },
) {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const listeningRef = useRef(false);
  const accumulatedRef = useRef('');
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const finalOnlyRef = useRef(opts?.finalOnly ?? false);
  finalOnlyRef.current = opts?.finalOnly ?? false;

  const supported =
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const getOrCreateRecognition = useCallback((): SpeechRecognitionInstance | null => {
    if (recognitionRef.current) return recognitionRef.current;

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return null;

    const recognition = new SR();
    // Single-utterance mode — we manually restart on end.
    // continuous:true causes duplication/glitching on Android Chrome
    // (words repeated 5-8x, text overwritten, cutting off mid-sentence).
    // The restart-on-end produces a beep on Android — that's a known
    // tradeoff until we switch to server-side STT on Android.
    recognition.continuous = false;
    recognition.interimResults = !finalOnlyRef.current;
    recognition.lang = navigator.language || 'en-US';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const transcript = event.results[0][0].transcript;
      const isFinal = event.results[0].isFinal;
      audioDebug('stt', `result: final=${isFinal} "${transcript.slice(0, 40)}"`);

      if (isFinal) {
        if (finalOnlyRef.current) {
          onTranscriptRef.current(transcript);
        } else {
          accumulatedRef.current += (accumulatedRef.current ? ' ' : '') + transcript;
          onTranscriptRef.current(accumulatedRef.current);
        }
      } else if (!finalOnlyRef.current) {
        const display = accumulatedRef.current
          ? accumulatedRef.current + ' ' + transcript
          : transcript;
        onTranscriptRef.current(display);
      }
    };

    recognition.onend = () => {
      audioDebug('stt', `onend — listening=${listeningRef.current}`);
      if (listeningRef.current) {
        audioDebug('stt', 'restarting recognition');
        try { recognition.start(); } catch { /* already running */ }
        return;
      }
      setIsListening(false);
    };

    recognition.onerror = (e: SpeechRecognitionErrorEvent) => {
      audioDebug('stt', `onerror: ${e.error}`);
      if (e.error === 'no-speech') return;
      if (e.error === 'aborted') return;
      listeningRef.current = false;
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    return recognition;
  }, []);

  const start = useCallback(() => {
    const recognition = getOrCreateRecognition();
    if (!recognition) return;
    audioDebug('stt', '▶ START called');
    accumulatedRef.current = '';
    listeningRef.current = true;
    setIsListening(true);
    recognition.start();
  }, [getOrCreateRecognition]);

  const stop = useCallback(() => {
    audioDebug('stt', '■ STOP called');
    listeningRef.current = false;
    // Set state immediately — do NOT rely on the onend event.
    // Android Chrome frequently fails to fire onend after .stop(),
    // which leaves the button stuck yellow permanently.
    setIsListening(false);
    recognitionRef.current?.stop();
  }, []);

  const toggle = useCallback(() => {
    if (isListening) stop();
    else start();
  }, [isListening, start, stop]);

  useEffect(() => {
    return () => {
      listeningRef.current = false;
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    };
  }, []);

  return { isListening, toggle, start, stop, supported };
}
